import { Stack } from "@mui/material";
import React from "react";
import Dropdown from "../../core/Dropdown";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Bar,
  BarChart,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";

import styles from "./StaffInfoChart.module.scss";
import { getStaffTrendsInterface } from "../../../apis/dashboard";
import { Option } from "../../../libs/types";
import { getUserOptions } from "../../../apis/brand-api";
import { UserResponseData } from "../../../apis/models/BrandlistResponse";
import { str2number } from "../../../libs/helpers";

interface StaffTrendsInterface {
  current: { staff_user_id: number; staff_user_name: string, total_quantity: string}[];
  previous: { staff_user_id: number; staff_user_name: string, total_quantity: string}[];
}

interface ChartDataType {
  staffId: number;
  staffName: string;
  currentQuantity: number;
  previousQuantity: number;
  currentColor: string;
  previousColor: string;
}

const createYearOptions = () => {
  const choices: Option[] = [];
  const minYear = 2022;
  const maxYear = new Date().getFullYear();
  for (let i = maxYear; i >= minYear; i--) {
    choices.push({ name: String(i), value: i});
  }

  return choices;
};

const createMonthOptions = () => {
  const choices: Option[] = [
    {name: "1月", value: 1},
    {name: "2月", value: 2},
    {name: "3月", value: 3},
    {name: "4月", value: 4},
    {name: "5月", value: 5},
    {name: "6月", value: 6},
    {name: "7月", value: 7},
    {name: "8月", value: 8},
    {name: "9月", value: 9},
    {name: "10月", value: 10},
    {name: "11月", value: 11},
    {name: "12月", value: 12},
  ];
  return choices;
};

const calcPieColor = (color: string, percent: number) => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = R + Math.round(R * ((100 - percent) / 100));
  G = G + Math.round(R * ((100 - percent) / 100));
  B = B + Math.round(R * ((100 - percent) / 100));

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  let RR =
    R.toString(16).length === 1 ? "0" + R.toString(16) : R.toString(16);
  let GG =
    G.toString(16).length === 1 ? "0" + G.toString(16) : G.toString(16);
  var BB =
    B.toString(16).length === 1 ? "0" + B.toString(16) : B.toString(16);

  return "#" + RR + GG + BB;
};

const StaffInfoChartComponent: React.FC = () => {
  const [category, setCategory] = React.useState<number>(0);
  const [year, setYear] = React.useState<number>(new Date().getFullYear());
  const [month, setMonth] = React.useState<number>(new Date().getMonth() + 1);
  const [apiData, setApiData] = React.useState<StaffTrendsInterface>();
  const [staffData, setStaffData] = React.useState<UserResponseData[]>([]);

  React.useEffect(() => {
    let isActive = true;
    const now = new Date();

    getUserOptions()
      .then(res => {
        if (isActive) {
          const userList: UserResponseData[] = res.data.data;
          setStaffData(userList);
        }
      })
      .catch(err => console.log(err))

    getStaffTrendsInterface(0, now.getFullYear(), now.getMonth() + 1)
    .then((res) => {
      if (isActive) {
        setApiData(res.data.data);
      }
    })
    .catch((err) => console.log(err));

    return () => {
      isActive = false;
    }
  }, []);

  const chartData = React.useMemo(() => {
    staffData.sort((a, b) => {
      if (a.aid === b.aid) {
        return 0;
      } else if (a.aid > b.aid) {
        return -1;
      } else {
        return 1;
      }
    })

    const currentBiggest = apiData?.current.reduce((prev, data) => Math.max(prev, str2number(data.total_quantity)), 0) ?? 0
    const previousBiggest = apiData?.previous.reduce((prev, data) => Math.max(prev, str2number(data.total_quantity)), 0) ?? 0

    return staffData.map(staff => {
      const currentData = apiData?.current.find(data => data.staff_user_id === staff.aid);
      const previousData = apiData?.previous.find(data => data.staff_user_id === staff.aid);
      const currentQuantity = currentData ? str2number(currentData.total_quantity) : 0;
      const previousQuantity = previousData ? str2number(previousData.total_quantity) : 0;

      return {
        staffId: staff.aid,
        staffName: staff.userName,
        currentQuantity: currentQuantity,
        previousQuantity: previousQuantity,
        currentColor: calcPieColor("#3A830F", currentQuantity / currentBiggest * 100),
        previousColor: calcPieColor("#1E53A3", previousQuantity / previousBiggest * 100)
      } as ChartDataType
    })
  }, [apiData, staffData]);

  const currentPieChartData = React.useMemo(() => {
    return chartData.filter((data) => data.currentQuantity !== 0)
  }, [chartData])

  const previousPieChartData = React.useMemo(() => {
    return chartData.filter((data) => data.previousQuantity !== 0)
  }, [chartData])

  async function onChangeYear (value: number) {
    setYear(value)
    const response = await getStaffTrendsInterface(category, value, month)
    setApiData(response.data.data)
  }

  async function onChangeMonth (value: number) {
    setMonth(value)
    const response = await getStaffTrendsInterface(category, year, value)
    setApiData(response.data.data)
  }

  async function onChangeCategory (value: number) {
    setCategory(value)
    const response = await getStaffTrendsInterface(value, year, month)
    setApiData(response.data.data)
  }

  return (
    <div className={styles.StaffInfoContainer}>
      <div className={styles.StaffInfoContent}>
        <div className={styles.title}>
          <h2>担当者別情報</h2>
        </div>
        <div className={styles.body}>
          <Stack spacing={2} style={{ width: "130px" }}>
            <Dropdown
              options={[
                { name: "坪数", value: 0 },
                { name: "入荷数", value: 1 },
                { name: "出庫数", value: 2 },
              ]}
              value={category}
              label="カテゴリー"
              onChange={(val) => onChangeCategory(val)}
            />
            <Dropdown
              options={createYearOptions()}
              value={year}
              label="年"
              onChange={(val) => onChangeYear(val)}
            />
            <Dropdown
              options={createMonthOptions()}
              value={month}
              label="月"
              onChange={(val) => onChangeMonth(val)}
            />
          </Stack>
          <div
            style={{ flexGrow: "1", display: "flex", flexDirection: "column" }}
          >
            <ResponsiveContainer width={"100%"} height={300}>
              <BarChart
                data={chartData}
                margin={{
                  top: 5,
                  right: 10,
                  left: 50,
                  bottom: 5,
                }}
              >
                <CartesianGrid stroke="#ccc" />
                <XAxis fontSize={12} dataKey="staffName" />
                <YAxis />
                <Tooltip />
                <Legend
                  iconType="square"
                  layout="vertical"
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{ paddingLeft: "30px" }}
                />
                <Bar
                  barSize={20}
                  dataKey={"currentQuantity"}
                  fill="#75A857"
                  name={`${year}年${month}月`}
                />
                <Bar
                  barSize={20}
                  dataKey={"previousQuantity"}
                  fill="#1E53A3"
                  name={`${year - 1}年${month}月`}
                />
              </BarChart>
            </ResponsiveContainer>
            <Stack direction="row" justifyContent={"space-evenly"} sx={{ marginTop: "30px", padding: "0 100px 0 50px" }}>
              <div className={styles.pieChart}>
                <span>{`${year - 1}年${month}月`}</span>
                <ResponsiveContainer>
                <PieChart>
                  <Pie
                    dataKey={"previousQuantity"}
                    isAnimationActive={true}
                    data={previousPieChartData}
                    startAngle={90}
                    endAngle={450}
                    outerRadius={80}
                    label={({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      value,
                      index,
                    }) => {
                      const RADIAN = Math.PI / 180;
                      const radius =
                        25 + innerRadius + (outerRadius - innerRadius);
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);

                      return (
                        value !== 0 ?
                        <text
                          x={x}
                          y={y}
                          fill="black"
                          textAnchor={x > cx ? "start" : "end"}
                          dominantBaseline="central"
                          fontSize={10}
                        >
                          {previousPieChartData[index].staffName} ({value})
                        </text>
                        :<></>
                      );
                    }}
                  >
                    {previousPieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.previousColor}
                      />
                    ))}
                  </Pie>
                </PieChart>
                </ResponsiveContainer>
              </div>
              <div className={styles.pieChart}>
                <span>{`${year}年${month}月`}</span>
                <ResponsiveContainer>
                <PieChart>
                  <Pie
                    dataKey={"currentQuantity"}
                    isAnimationActive={true}
                    data={currentPieChartData}
                    startAngle={90}
                    endAngle={450}
                    outerRadius={80}
                    fill="#75A857"
                    label={({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      value,
                      index,
                    }) => {
                      const RADIAN = Math.PI / 180;
                      const radius =
                        25 + innerRadius + (outerRadius - innerRadius);
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);

                      return (
                        value !== 0 ?
                        <text
                          x={x}
                          y={y}
                          fill="black"
                          textAnchor={x > cx ? "start" : "end"}
                          dominantBaseline="central"
                          fontSize={10}
                        >
                          {currentPieChartData[index].staffName} ({value})
                        </text>
                        :<></>
                      );
                    }}
                  >
                    {currentPieChartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.currentColor}
                      />
                    ))}
                  </Pie>
                </PieChart>
                </ResponsiveContainer>
              </div>
            </Stack>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffInfoChartComponent;
